import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@clerk/tanstack-react-start";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { Button } from "~/components/ui/button";
import { getCheckoutStatus } from "~/lib/stripe";

const searchSchema = z.object({
	session_id: z.string().optional(),
});

const MAX_POLL_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 2000;

type CheckoutStatusResult = Awaited<ReturnType<typeof getCheckoutStatus>>;

export const Route = createFileRoute("/stripe/success")({
	validateSearch: (search) => searchSchema.parse(search),
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = useNavigate();
	const { isLoaded, isSignedIn } = useAuth();
	const { session_id: sessionId } = Route.useSearch();
	const [status, setStatus] = useState<CheckoutStatusResult | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [isPolling, setIsPolling] = useState(true);
	const [refreshKey, setRefreshKey] = useState(0);

	const redirectTarget = useMemo(() => {
		if (typeof window === "undefined") return "/stripe/success";
		return `${window.location.pathname}${window.location.search}`;
	}, []);

	useEffect(() => {
		if (!isLoaded) return;
		if (!isSignedIn) {
			navigate({
				to: "/sign-in",
				search: { redirect: redirectTarget },
			});
		}
	}, [isLoaded, isSignedIn, navigate, redirectTarget]);

	useEffect(() => {
		if (!isLoaded || !isSignedIn) return;
		if (!sessionId) {
			setIsPolling(false);
			setError(
				"Missing Stripe session details. Please return to the dashboard.",
			);
			return;
		}

		let cancelled = false;

		const pollCheckoutStatus = async () => {
			setError(null);
			setIsPolling(true);

			for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt += 1) {
				try {
					const result = await getCheckoutStatus({
						data: { sessionId },
					});

					if (cancelled) return;

					setStatus(result);

					if (result.isSynced && result.guildId) {
						setIsPolling(false);
						navigate({
							to: "/dashboard/$guildId",
							params: { guildId: result.guildId },
						});
						return;
					}
				} catch (cause) {
					if (cancelled) return;

					const message =
						cause instanceof Error
							? cause.message
							: "We couldn't verify your Stripe checkout.";
					setError(message);
					setIsPolling(false);
					return;
				}

				if (attempt < MAX_POLL_ATTEMPTS - 1) {
					await new Promise((resolve) =>
						window.setTimeout(resolve, POLL_INTERVAL_MS),
					);
				}
			}

			if (!cancelled) setIsPolling(false);
		};

		void pollCheckoutStatus();

		return () => {
			cancelled = true;
		};
	}, [isLoaded, isSignedIn, navigate, refreshKey, sessionId]);

	const guildId = status?.guildId ?? null;
	const showPendingSync =
		!error &&
		((status !== null && !status.isSynced) || (isPolling && sessionId));

	return (
		<div className="min-h-screen">
			<div className="pointer-events-none fixed inset-0 flex items-start justify-center overflow-hidden">
				<div className="mt-[-100px] h-[500px] w-[800px] rounded-full bg-primary/8 blur-[120px]" />
			</div>

			<div className="relative mx-auto flex min-h-[calc(100vh-4rem)] max-w-xl items-center px-6 py-16">
				<div className="w-full rounded-3xl border border-border bg-card p-8 shadow-xl shadow-primary/5">
					<div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
						{showPendingSync ? (
							<Loader2 className="h-7 w-7 animate-spin" />
						) : (
							<CheckCircle2 className="h-7 w-7" />
						)}
					</div>

					<h1 className="text-3xl font-extrabold tracking-tight text-foreground">
						{error
							? "We couldn't finish your subscription"
							: showPendingSync
								? "Finalizing your subscription"
								: "Subscription confirmed"}
					</h1>

					<p className="mt-3 text-sm text-muted-foreground">
						{error
							? error
							: showPendingSync
								? "Your payment completed successfully. We're waiting for Overseer to sync the new subscription to your server."
								: "Your subscription is active and ready to manage from the dashboard."}
					</p>

					{status && !error && (
						<div className="mt-6 rounded-2xl border border-border/80 bg-secondary/30 p-4 text-sm">
							<div className="flex items-center justify-between gap-3">
								<span className="text-muted-foreground">
									Checkout
								</span>
								<span className="font-semibold capitalize text-foreground">
									{status.sessionStatus ?? "complete"}
								</span>
							</div>
							<div className="mt-2 flex items-center justify-between gap-3">
								<span className="text-muted-foreground">
									Payment
								</span>
								<span className="font-semibold capitalize text-foreground">
									{status.paymentStatus}
								</span>
							</div>
							{status.subscriptionStatus && (
								<div className="mt-2 flex items-center justify-between gap-3">
									<span className="text-muted-foreground">
										Subscription
									</span>
									<span className="font-semibold capitalize text-foreground">
										{status.subscriptionStatus}
									</span>
								</div>
							)}
						</div>
					)}

					<div className="mt-8 flex flex-col gap-3 sm:flex-row">
						<Button
							className="sm:flex-1"
							onClick={() => setRefreshKey((value) => value + 1)}
							disabled={isPolling || !sessionId}
						>
							{isPolling ? "Checking status..." : "Check again"}
						</Button>
						<Button
							variant="secondary"
							className="sm:flex-1"
							asChild
						>
							<Link
								to={
									guildId
										? "/dashboard/$guildId"
										: "/dashboard"
								}
								params={guildId ? { guildId } : undefined}
							>
								{guildId
									? "Open server dashboard"
									: "Back to dashboard"}
							</Link>
						</Button>
					</div>

					{showPendingSync && (
						<p className="mt-4 text-xs text-muted-foreground">
							This page will keep checking for a few seconds. If
							the subscription webhook is still processing, use
							"Check again" or head back to the dashboard.
						</p>
					)}
				</div>
			</div>
		</div>
	);
}
